import { useDispatch, useSelector } from 'react-redux';
import i18n from '../../../languages/i18n';
import ButtonPrimaryLarge from '../ButtonPrimary/ButtonPrimaryLarge';
import { openModal } from './openModal';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import Tag from '../Tag/Tag';
import ButtonPrimaryMedium from '../ButtonPrimary/ButtonPrimaryMedium';
import {
  confirmRefund,
  createRefund,
  fetchOrderAdmin,
} from '../../../store/actions/admin.room.action';
import { openMessageSuccess } from '../Message/openMessageSuccess';
import { openMessageError } from '../Message/openMessageError';
import Table from '../Table/Table';
import ButtonTertiaryMedium from '../ButtonTertiary/ButtonTertiaryMedium';
import { cn } from '../../../utils/string/cn.utils';
import { Divider } from 'antd';
import { convertForeignCurrency } from '../../../utils/currency/currency.utils';

const openModalRefundInformation = ({ onClose }) => {
  const modal = openModal({
    title: (
      <span className="text-headline-small">
        {i18n.t('Payment & refund information')}
      </span>
    ),
    content: (
      <>
        <Divider type="horizontal" className="!p-0 m-0 pt-[16px]" />
        <div className="px-[24px] !py-[16px] flex flex-col items-start gap-[32px] grow self-stretch">
          <FormRefundInformation />
        </div>
      </>
    ),
    centered: true,
    onClose: () => {
      onClose?.();
    },
    width: 1024,
    classNames: {
      header: '!p-[24px] !m-0',
      footer: '!p-[24px] gap-[16px] items-end flex justify-end',
      body: '!px-[0px] !py-[0px] flex flex-col items-start gap-[0px] grow self-stretch h-[510px] overflow-auto',
    },
    footer: <></>,
  });
};

export default openModalRefundInformation;

const FormRefundInformation = () => {
  const { t } = useTranslation();
  const { order, refund } = useSelector(state => state.adminRoom);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (order.detail?.refund_transactions?.length) {
      setData(order.detail?.refund_transactions);
    } else {
      setData(order.detail?.payment_transactions);
    }
  }, [order]);

  return (
    <>
      <Spin fullscreen spinning={refund.isLoading} size="large" />
      <div className="flex justify-between items-center self-stretch">
        <div className="flex items-center gap-[16px]">
          <span className="text-title-small">{t('Payment history')}</span>
          <Tag
            label={
              <span className="text-label-medium font-semibold text-white">
                {order.detail?.type === 1
                  ? t('Pay the full order amount')
                  : t('Progress payment')}
              </span>
            }
            className="bg-[#258FD2] rounded-[1000px]"
          />
        </div>
        <ButtonPrimaryMedium
          title={t('Create refund request')}
          disabled={
            order.detail?.refund_transactions?.length ||
            order.detail?.payment_transactions?.length === 0 ||
            refund.isLoading
          }
          isLoading={refund.isLoading}
          onClick={async () => {
            try {
              dispatch(createRefund({ order_code: order.detail?.code })).then(
                async action => {
                  if (action.payload?.data) {
                    openMessageSuccess({ content: t('Successfully') });
                    dispatch(fetchOrderAdmin({ id: order.detail?.id }));
                  } else {
                    throw t('Failed');
                  }
                }
              );
            } catch (error) {
              openMessageError({ content: error || t('Failed') });
            }
          }}
        />
      </div>
      <Table
        dataSource={[...data].map((item, index) => ({
          ...item,
          key: index,
        }))}
        columns={[
          {
            title: t('Payment installment'),
            dataIndex: '',
            key: '',
            minWidth: 136,
            render: (_, _1, index) => {
              return (
                <div className="flex py-[10px] ps-[16px] pe-[24px] items-center gap-[4px] self-stretch">
                  <span className="text-body-medium">{index + 1}</span>
                </div>
              );
            },
          },
          {
            title: t('Payment code'),
            dataIndex: '',
            key: '',
            minWidth: 240,
            render: (_, record) => {
              return (
                <div className="flex py-[10px] ps-[16px] pe-[24px] items-center gap-[4px] self-stretch">
                  <span className="text-body-medium">
                    {record.raw_response?.vpc_TransactionNo}
                  </span>
                </div>
              );
            },
          },
          {
            title: t('Payment amount'),
            dataIndex: '',
            key: '',
            minWidth: 200,
            render: (_, record) => {
              return (
                <div className="flex py-[10px] ps-[16px] pe-[24px] items-center gap-[4px] self-stretch">
                  <span className="text-body-medium font-semibold">
                    {convertForeignCurrency(record.amount)}
                  </span>
                </div>
              );
            },
          },
          {
            title: t('Status'),
            dataIndex: '',
            key: '',
            minWidth: 200,
            render: (_, record) => {
              const requested = order.detail?.refund_transactions?.length > 0;
              const state = !requested
                ? 0
                : record.status === 'pending' || record.status === 0
                  ? 0
                  : record.status === 'success' || record.status === 1
                    ? 1
                    : null;
              return (
                <div className="flex py-[10px] ps-[16px] pe-[24px] items-center gap-[4px] self-stretch">
                  <Tag
                    label={
                      <span className="text-body-medium font-semibold text-white">
                        {state === 0
                          ? t('Pending refund')
                          : state === 1
                            ? t('Refunded')
                            : t('Error.unknown_error')}
                      </span>
                    }
                    className={cn(
                      'bg-[#49AC3E] rounded-[1000px]',
                      state === 0 && 'bg-[#F97D16]',
                      state === null && 'bg-[#E5654E]'
                    )}
                  />
                </div>
              );
            },
          },
          {
            title: t('Feature'),
            dataIndex: '',
            key: '',
            minWidth: 200,
            render: (_, record) => {
              return (
                <div className="flex py-[10px] ps-[16px] pe-[24px] items-center gap-[4px] self-stretch">
                  <ButtonTertiaryMedium
                    title={t('Mark as completed')}
                    disabled={record.id && record.status === 0 ? false : true}
                    onClick={() => {
                      dispatch(confirmRefund({ refund_id: record.id })).then(
                        async action => {
                          if (action.payload?.data) {
                            openMessageSuccess({
                              content: t('Successfully'),
                            });
                            dispatch(fetchOrderAdmin({ id: order.detail?.id }));
                          } else {
                            openMessageError({ content: t('Failed') });
                          }
                        }
                      );
                    }}
                  />
                </div>
              );
            },
          },
        ]}
        // scroll={{ x: '100vw' }}
        // className={styles.customTable}
        loading={order.isLoading}
        // virtual={true}
        pagination={false}
        onRow={(record, rowIndex) => {
          // return {
          //   onClick: event => {
          //     if (record?.id)
          //       navigate(ROUTER.CMS + ROUTER.ORDER + '/' + record.id);
          //   }, // click row
          //   onDoubleClick: event => {}, // double click row
          //   onContextMenu: event => {}, // right button click row
          //   onMouseEnter: event => {}, // mouse enter row
          //   onMouseLeave: event => {}, // mouse leave row
          // };
        }}
        rowSelection={false}
      />
    </>
  );
};
